import * as React from "react";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { decode } from "html-entities";

import { getOnlineNode } from "../../../core/getNode";
import { paginator } from "../../../core/paginator";
import { alertMessages } from "../../../partials/alertMessages";
import { useEmailDataContext } from "../../../partials/dataContext";
import Pagination from "../../../partials/Pagination";
import MessageBox from "../../../partials/MessageBox";
import WebformItem from "./WebformItem";
import SelectedWebformItem from "./SelectedWebformItem";
import LoadingSpinner from "../../../partials/loading";
import { getContentTypeByBundle, getTemplateByIdentity } from "../../../utils/helpers";

import { Button, Select, FormControl, InputLabel, MenuItem } from "@mui/material";

export default function Webforms(props) {
  const [webformCategory, setWebformCategory] = useState(0);

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPaginating, setIsPaginating] = useState(false);
  const [isError, setIsError] = useState(false);

  const [errorStatus, setErrorStatus] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [index, setIndex] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const [categories, setCategories] = useState([]);
  const [selectedWebformItems, setSelectedWebformItems] = useState([]);

  const [data, setData] = useState([]);

  const [messageBoxVisible, setMessageBoxVisible] = useState(false);
  const [messageType, setMessageType] = useState("");
  const [messageTitle, setMessageTitle] = useState("");
  const [messageBody, setMessageBody] = useState("");

  const [sessionData, setSessionData] = useState(JSON.parse(localStorage.getItem("sessionData")));

  const { selectedTemplate, contentTypeSelected, setWebformsObject } = useEmailDataContext();

  // redux
  const system = useSelector((state) => state).systemReducer;
  const config = useSelector((state) => state.configReducer);
  const templateData = getTemplateByIdentity(selectedTemplate, config.templates);
  const contentTypeData = getContentTypeByBundle(contentTypeSelected, templateData.contentTypes);

  useEffect(() => {
    getCategories();
  }, []);

  useEffect(() => {
    getContent();
  }, [currentPage]);

  useEffect(() => {
    if (selectedWebformItems.length > 0) {
      setWebformsObject(selectedWebformItems.map((item) => item.nid));
    } else {
      setWebformsObject([]);
    }
  }, [selectedWebformItems]);

  const messageBoxState = (visible, type, title, body) => {
    setMessageBoxVisible(visible);
    setMessageType(type);
    setMessageTitle(title);
    setMessageBody(body);
  };

  const getCategories = () => {
    setIsLoading(true);

    getOnlineNode("api/v2/taxonomies/webform_categories")
      .then((response) => {
        renderCategories(response.data);
      })
      .catch((_error) => {
        if (_error.response) {
          messageBoxState(true, "error", "Error", _error.response.statusText);
        } else if (_error.request) {
          messageBoxState(true, "error", "Error", alertMessages.requestError.message);
        } else {
          messageBoxState(true, "error", "Error", alertMessages.unknownError.message);
        }
        setIsLoading(false);
      });
  };

  const renderCategories = (_data) => {
    let _categories = [
      {
        value: 0,
        label: "--- All ---",
      },
    ];

    _data.forEach((item) => {
      _categories.push({
        value: item?.tid[0].value,
        label: item?.name[0].value,
      });
    });
    setCategories(_categories);
  };

  const getContent = () => {
    setIsLoading(true);
    messageBoxState(false, "", "", "");

    let path = "api/v2/webforms?status=1&promote=1" + "&items_per_page=" + pageSize + "&page=" + (currentPage - 1);

    const tid = webformCategory;

    if (tid !== 0) {
      path =
        "api/v2/webforms/" +
        tid +
        "/all" +
        "?status=1&promote=1" +
        "&items_per_page=" +
        pageSize +
        "&page=" +
        (currentPage - 1);
    }

    getOnlineNode(path)
      .then((response) => {
        setData(response.data.rows);
        setTotalItems(response.data.pager.total_items);
        setIsLoading(false);
        setIsRefreshing(false);
        setIsPaginating(false);
      })
      .catch((_error) => {
        if (_error.response) {
          messageBoxState(true, "error", "Error", _error.response.statusText);
        } else if (_error.request) {
          messageBoxState(true, "error", "Error", alertMessages.requestError.message);
        } else {
          messageBoxState(true, "error", "Error", alertMessages.unknownError.message);
        }
        setIsLoading(false);
      });
  };

  const addSelectedWebformItem = (_data) => {
    if (
      !(
        selectedWebformItems.filter((item) => {
          return item.nid === _data.nid;
        }).length > 0
      )
    ) {
      setSelectedWebformItems((prevItems) => [...prevItems, _data]);
    }
  };

  const removeSelectedWebformItem = (webform) => {
    setSelectedWebformItems((prevItems) => prevItems.filter((item) => item.nid !== webform.nid));
  };

  const renderPagination = () => {
    if (data.length && data.length > 0) {
      return (
        <Pagination
          containerClasses={`d-flex justify-content-center align-items-center`}
          previousDisabled={currentPage === 1 ? true : false}
          nextDisabled={data?.length < pageSize ? true : false}
          currentPage={currentPage}
          onPrevious={() => {
            const current = currentPage - 1;
            setCurrentPage(current);
          }}
          onNext={() => {
            const current = currentPage + 1;
            setCurrentPage(current);
          }}
        />
      );
    }
  };

  const updateWebformItemsView = (event) => {
    if (event) {
      event.preventDefault();
    }
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      getContent();
    }
  };

  const removeAllSelectedWebformItems = (event) => {
    if (event) {
      event.preventDefault();
    }
    setSelectedWebformItems([]);
  };

  const renderCategoryList = categories.map((category) => {
    return (
      <MenuItem
        key={`category-${category.value}`}
        value={category.value}
        onClick={(e) => {
          setWebformCategory(e.target.value);
        }}
      >
        {decode(category.label)}
      </MenuItem>
    );
  });

  const renderSelectedWebformItems = selectedWebformItems.map((webform) => (
    <SelectedWebformItem
      key={`selected-webform-item-${webform.nid}`}
      webform={webform}
      removeSelectedWebformItem={(item) => removeSelectedWebformItem(item)}
    />
  ));

  const renderWebformItems = data.map((webform) => (
    <WebformItem
      webform={webform}
      key={webform.nid}
      selectedWebforms={selectedWebformItems}
      addSelectedWebformItem={(item) => addSelectedWebformItem(item)}
      removeSelectedWebformItem={(item) => removeSelectedWebformItem(item)}
    />
  ));

  return (
    <div className="webform-view-wrapper">
      {isLoading && (
        <div
          className="w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ position: "absolute", top: 0, left: 0, backgroundColor: "rgba(255,255,255,0.5)", zIndex: 9999 }}
        >
          <LoadingSpinner />
        </div>
      )}
      <div className="message-box-wrapper">
        <MessageBox messageBoxVisible={messageBoxVisible} messageTitle={messageTitle} messageBody={messageBody} />
      </div>
      <div className="webform-form-filters-wrapper">
        <div className="available-presentations-title">
          <span>Choose Forms</span>
        </div>
        <form className="filter-form">
          <div className="form-item-wrapper input category">
            <FormControl className="filter-select" size="small">
              <InputLabel sx={{ fontSize: "14px" }} size="small" id="category-select-label">
                Category
              </InputLabel>
              <Select
                labelId="category-select-label"
                id="category-select"
                value={webformCategory}
                label="Category"
                sx={{
                  fontSize: "14px",
                }}
                onChange={(e) => {
                  setWebformCategory(e.target.value);
                }}
              >
                {renderCategoryList}
              </Select>
            </FormControl>
          </div>
          <div className="form-item-wrapper">
            <div className="form-submit-wrapper form-button-wrapper form-control-wrapper">
              <Button
                type="submit"
                onClick={updateWebformItemsView}
                variant="contained"
                sx={{
                  backgroundColor: "#1d9bf1",
                  width: "100%",
                  lineHeight: "14px",
                  height: "35px",
                  fontWeight: "500",
                  fontSize: "14px",
                  textTransform: "none",
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </form>
      </div>
      <div id="webform-view-content" className="webform-view-content">
        <div className="webform-items-wrapper">
          <div id="webform-items">{renderWebformItems}</div>
          <div className="">{renderPagination()}</div>
        </div>
        <div className="selected-webform-header-wrapper">
          <span>Selected Webforms</span>
          {selectedWebformItems.length > 0 ? (
            <Button
              variant="outlined"
              size="small"
              sx={{
                textTransform: "none",
                fontSize: "14px",
              }}
              className="selected-webform-clear-all-button"
              onClick={() => removeAllSelectedWebformItems()}
            >
              Clear all
            </Button>
          ) : null}
        </div>
        <div id="webform-items-selected-by-user-wrapper" className="webform-items-selected-by-user-wrapper">
          <div id="webform-items-selected-by-user" className="webform-items-selected-by-user">
            {selectedWebformItems.length > 0 ? (
              ""
            ) : (
              <>
                <h5 className="instruction title">INSTRUCTIONS</h5>
              </>
            )}
            {selectedWebformItems.length > 0 ? (
              <>
                {selectedWebformItems.map((webform, index) => (
                  <SelectedWebformItem
                    key={`selected-webform-item-${index}`}
                    removeSelectedWebformItem={(webform) => removeSelectedWebformItem(webform)}
                    webform={webform}
                  />
                ))}
              </>
            ) : (
              <>{contentTypeData.help && <>{contentTypeData.help}</>}</>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
